import Vue from 'vue'
import moment from 'moment'
import { MutationTree } from 'vuex'
import auth from '../auth'
import { breakpoints } from '@/constants'
import { clearCache } from './caching'
import { state, getResetState } from './state'
import { generatePartId, generateSalesOrderId } from '@/helpers'
import { MAX_RECORDSET } from '@/constants'
import { Part, PullRequest, Shipment } from '@/interfaces'

export const mutations: MutationTree<typeof state> = {
  resetData(state) {
    clearCache()
    const resetState = getResetState()
    Object.keys(resetState).forEach(key => {
      ;(state as any)[key] = (resetState as any)[key]
    })
  },

  setParts(state, { data, isLoading, hasError }) {
    // add partId and update parts
    state.parts = {
      isLoading: !!isLoading,
      hasError: !!hasError,
      data: Array.isArray(data)
        ? data.slice(0, MAX_RECORDSET).map((part: Part, index: number) => {
            var id = generatePartId(part)
            return { ...part, partId: id }
          })
        : [],
    }
  },

  setAccount(state, customer) {
    // update customer account
    state.customer = customer
  },

  setAccountId(state, accountId: string) {
    state.accountId = accountId
  },

  setAccountList(state, accounts) {
    Vue.set(state, 'accountList', accounts)
  },

  setMessages(state, messages) {
    state.notifications = {
      ...messages,
      messages: messages.messages.slice(0, MAX_RECORDSET).map((message: any) => {
        const preview = message.body
          .replace(/(<([^>]+)>)/gi, '')
          .substring(0, 70)
          .concat('...')
        return { ...message, preview, isChecked: false }
      }),
    }
  },

  setMessagesDelete(state, ids) {
    const filteredMessages = state.notifications.messages.filter((n: any) => !ids.includes(n.id))
    const updatedAlertCount = filteredMessages.filter((n: any) => n.isAlert == true).length
    state.notifications = {
      ...state.notifications,
      alertCount: updatedAlertCount,
      messages: filteredMessages,
    }
  },

  setMessagesRead(state, ids) {
    const filteredMessages = state.notifications.messages.map((n: any) => {
      return { ...n, isRead: ids.includes(n.id) ? true : n.isRead }
    })
    state.notifications = { ...state.notifications, messages: filteredMessages }
  },

  setMessagesClearAlerts(state, ids) {
    const filteredMessages = state.notifications.messages.map((n: any) => {
      return { ...n, isAlert: ids.includes(n.id) ? false : n.isAlert }
    })
    const updatedAlertCount = filteredMessages.filter((n: any) => n.isAlert == true).length
    state.notifications = {
      ...state.notifications,
      alertCount: updatedAlertCount,
      messages: filteredMessages,
    }
  },

  setEmailList(state, list) {
    Vue.set(state, 'emailList', list)
  },

  setNavigationVisibility(state, isVisible) {
    Vue.set(state.navigation, 'isVisible', isVisible)
  },

  setDashboardHeaderVisibility(state, isVisible) {
    Vue.set(state.dashboard, 'headerVisible', isVisible)
  },

  setPurchaseOrders(state, { data, isLoading, hasError }) {
    state.purchaseOrders = {
      isLoading: !!isLoading,
      hasError: !!hasError,
      data: Array.isArray(data) ? data.slice(0, MAX_RECORDSET) : [],
    }
  },

  setReplenishments(state, { data, isLoading, hasError }) {
    state.replenishmentView = {
      ...data,
      isLoading: !!isLoading,
      hasError: !!hasError,
      data: Array.isArray(data.replenishments)
        ? data.replenishments.slice(0, MAX_RECORDSET).map(function(rep: any) {
            const id = generatePartId(rep)
            return { ...rep, partId: id }
          })
        : [],
      rawData: Array.isArray(data.rawReplenishments)
        ? data.rawReplenishments.slice(0, MAX_RECORDSET).map(function(rep: any) {
            const id = generatePartId(rep)
            const calendarWeekArr = rep.calendarWeek.split('-')
            return {
              ...rep,
              partId: id,
              calendarWeekSort: calendarWeekArr[1] + ('0' + calendarWeekArr[0]).slice(-2),
            }
          })
        : [],
    }
  },

  setPullRequests(state, { data, isLoading, hasError }) {
    state.pullRequests = {
      isLoading: !!isLoading,
      hasError: !!hasError,
      data: Array.isArray(data)
        ? data.slice(0, MAX_RECORDSET).map((pullRequest: PullRequest) => {
            const partId = generatePartId(pullRequest)
            const pullId = generateSalesOrderId(pullRequest)
            const dateShippedOrAvailable =
              pullRequest.dateShipped || pullRequest.materialAvailableDate
            return {
              ...pullRequest,
              partId: partId,
              pullId: pullId,
              dateShippedOrAvailable: dateShippedOrAvailable,
            }
          })
        : [],
    }
  },

  setShipment(state, { data, isLoading, hasError }) {
    state.shipments = {
      data: Array.isArray(data)
        ? data.slice(0, MAX_RECORDSET).map((shipment: Shipment) => {
            const shipmentId = generateSalesOrderId(shipment)
            return { ...shipment, shipmentId: shipmentId }
          })
        : [],
      isLoading: !!isLoading,
      hasError: !!hasError,
    }
  },

  setInventoryView(state, view) {
    state.currentInventoryView = view
  },

  setShipmentsView(state, view) {
    state.currentShipmentsView = view
  },

  setPullsView(state, view) {
    state.currentPullsView = view
  },

  setPullsHistoryView(state, view) {
    state.currentPullsHistoryView = view
  },

  setReplenishmentsView(state, view) {
    state.currentReplenishmentsView = view
  },

  setCurrentSubtab(state, currentSubtab) {
    state.currentSubtabs = state.currentSubtabs.map((subtab: any, index: number) => {
      return {
        ...subtab,
        subtab: subtab.parent === currentSubtab.parent ? currentSubtab.subtab : subtab.subtab,
      }
    })
  },

  setWindowSize(state, size) {
    state.windowSize = size
    const { width } = size
    state.currentBreakpoint =
      width >= breakpoints.xl
        ? 'xl'
        : width >= breakpoints.lg
        ? 'lg'
        : width >= breakpoints.md
        ? 'md'
        : width >= breakpoints.sm
        ? 'sm'
        : 'xs'
  },

  setCSOFlag(state) {
    state.isCSO = auth.getUserProfile().roles.indexOf('Micron') > -1
  },

  setUserName(state) {
    state.userName = auth.getUserProfile().name
  },

  setSelectedPart(state, isVisible) {
    //state.selectedpart=auth.getUserProfile().name
    Vue.set(state.selectedpart, 'isVisible', isVisible)
  },
}
